/**
 * The programme's governance record, and every action taken on it.
 *
 * @module components/governance/useProgrammeGovernance
 */

import { useCallback, useEffect, useState } from "react";

import {
  type InitialApprovalDetails,
  validateInitialApprovalDetails,
} from "@/components/shared/InitialApprovalFields";
import type {
  EditorProgrammeMetadata,
  ProgrammeGovernanceSummary,
  ProgrammePlatformClient,
  ProgrammeReviewRoundTimeline,
  ProgrammeSummary,
  SessionContext,
} from "@/platform";
import { can } from "@/server/authorization";
import { blockersForGate, meetsReadinessGate } from "@/server/readiness-ladder";
import type { ExportFormat, ReadinessState } from "@/server/types";
import { activeSteps, activeViewsOutputs } from "@/state/store";
import { begin } from "@/utils/begin";
import { uid } from "@/utils/uid";

import { finalExportRefusal, finalSignOffActions, getWorkflowActions } from "./governance-actions";
import { approvalStationIndex, stageGuidance } from "./governance-stations";
import { useGovernanceApplicability } from "./useGovernanceApplicability";
import { useGovernanceOverride } from "./useGovernanceOverride";

export interface ProgrammeGovernanceInputs {
  client: ProgrammePlatformClient;
  session: SessionContext;
  programme: EditorProgrammeMetadata;
  onProgrammeChanged: () => Promise<void>;
  onRevisionCreated: (programmeId: string) => Promise<void>;
}

export function useProgrammeGovernance({
  client,
  session,
  programme,
  onProgrammeChanged,
  onRevisionCreated,
}: ProgrammeGovernanceInputs) {
  const [governance, setGovernance] = useState<ProgrammeGovernanceSummary | null>(null);
  const [reviewRounds, setReviewRounds] = useState<ProgrammeReviewRoundTimeline[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [actionPending, setActionPending] = useState(false);
  const [workflowNote, setWorkflowNote] = useState("");
  const overrideState = useGovernanceOverride();
  const {
    overrideActionId,
    overrideReason,
    overrideConfirmed,
    setOverrideReference,
    closeOverride,
  } = overrideState;
  const [exportFormat, setExportFormat] = useState<ExportFormat>("descriptor");
  const [expanded, setExpanded] = useState(
    () => window.location.hash === "#readiness-and-approval",
  );
  const applicabilityState = useGovernanceApplicability();
  const {
    effectiveFrom,
    setEffectiveFrom,
    applicabilityRevisionId,
    setApplicabilityRevisionId,
    publicationConsent,
    setPublicationConsent,
  } = applicabilityState;
  const [approvalDetails, setApprovalDetails] = useState<InitialApprovalDetails>({
    institutionalLabel: "Initial Validation",
    effectiveFrom: "",
    nextRevalidationDue: "",
  });

  // Fetch and publish, with no synchronous prologue and every update in a
  // continuation, so the mount effect below can call it without cascading a
  // second render before the first has painted. `loading` already starts true
  // and `error` null, so the mount path needs no prologue.
  const fetchGovernance = useCallback(
    (): Promise<void> =>
      begin(() =>
        Promise.all([
          client.getProgrammeGovernance(programme.id),
          client.listProgrammeReviewRounds(programme.id),
        ]),
      )
        .then(([governanceSummary, reviewRoundTimeline]) => {
          setGovernance(governanceSummary);
          setReviewRounds(reviewRoundTimeline ?? []);
          setError(null);
        })
        .catch((cause: unknown) => {
          setError(
            cause instanceof Error ? cause : new Error("Unable to load programme governance"),
          );
        })
        .finally(() => {
          setLoading(false);
        }),
    [client, programme.id],
  );

  // The reload an action wants. It keeps the readiness already on screen:
  // raising `loading` again would swap the panel for a spinner mid-action. With
  // nothing on screen, after a failed first read, the spinner is all there is
  // to say it is reading.
  const refresh = useCallback(async (): Promise<void> => {
    setError(null);
    if (!governance) {
      setLoading(true);
    }
    await fetchGovernance();
  }, [fetchGovernance, governance]);

  // Read again when the workflow moves, wherever it was moved from: the
  // actions offered belong to the state, and the panel is not remounted.
  useEffect(() => {
    fetchGovernance();
  }, [fetchGovernance, programme.workflowState]);

  /** Whether it landed, since the note a caller consumed is cleared only then. */
  const runAction = async (action: () => Promise<unknown>): Promise<boolean> => {
    setActionPending(true);
    setError(null);
    try {
      await action();
      await onProgrammeChanged();
      await refresh();
      return true;
    } catch (cause) {
      // A refused action usually means the programme moved on, and every retry
      // from the stale revision is refused the same way. Re-read it first, so
      // the next attempt carries the current revision and the reload cannot
      // clear the failure that is about to be reported.
      await Promise.resolve(onProgrammeChanged()).catch(() => undefined);
      // The governance record too, as the change case's own hook does: a
      // refusal usually means a finding was raised or a sign-off landed, and
      // the panel would otherwise keep offering the action unblocked.
      await refresh().catch(() => undefined);
      setError(cause instanceof Error ? cause : new Error("Approval process action failed"));
      return false;
    } finally {
      setActionPending(false);
    }
  };

  const leadSigned = governance?.signOffs.some((signOff) => signOff.capacity === "lead") ?? false;
  const qaSigned = governance?.signOffs.some((signOff) => signOff.capacity === "qa") ?? false;
  const canSignLead = session.subject.responsibilities.some(
    (assignment) => assignment.programmeId === programme.id && assignment.responsibility === "lead",
  );
  const canSignQa =
    session.subject.accountRole === "client-admin" ||
    session.subject.accountRole === "platform-admin";
  const exportRefusal = finalExportRefusal(
    canSignLead,
    programme.workflowState,
    governance?.readiness.state,
  );
  const canExport = exportRefusal === undefined;
  const canCreateRevision =
    session.subject.accountRole === "client-admin" ||
    session.subject.accountRole === "platform-admin" ||
    canSignLead;
  const submissionBlockers = governance
    ? blockersForGate(governance.readiness.blockers, "Ready for internal review")
    : [];
  const submissionBlockerCount = submissionBlockers.length;
  const submissionBlocked = governance
    ? !meetsReadinessGate(governance.readiness.state, "Ready for internal review")
    : true;
  const approvalBlockers = governance
    ? blockersForGate(governance.readiness.blockers, "Ready for approval")
    : [];
  const approvalBlocked = governance
    ? !meetsReadinessGate(governance.readiness.state, "Ready for approval")
    : true;
  const approvalCandidate = governance?.approvalCandidates.at(-1);
  const submittedRevision = governance?.governanceRevisions
    .filter((revision) => revision.kind === "submitted")
    .at(-1);
  const approvedRevision = governance?.governanceRevisions
    .filter((revision) => revision.kind === "approved")
    .at(-1);
  const canSetApplicability =
    approvedRevision !== undefined &&
    can(session.subject, "programme:approve", { type: "programme", programmeId: programme.id });
  const navigationItems = [...activeSteps(), ...activeViewsOutputs()];
  const stage = stageGuidance(programme.workflowState);
  const currentStationIndex = approvalStationIndex(programme.workflowState);

  const queueProgramme: ProgrammeSummary = {
    id: programme.id,
    tenantId: session.tenant.id,
    title: "Programme",
    awardType: "",
    nfqLevel: null,
    totalCredits: 0,
    discipline: "",
    moduleCount: 0,
    workflowState: programme.workflowState,
    visibilityState: programme.visibilityState,
    saveRevision: programme.saveRevision,
    createdBy: session.user.id,
  };
  const blockingReviewFindingCount = governance?.blockingFindings.length ?? 0;
  // Mirrors recordProgrammeReviewOutcome's own `rounds.at(-1)`
  // (governance-operations.ts): both read the same round list, so this is
  // the same "current round" the server checks outcome submissions against.
  const currentRoundReviewerUserIds = reviewRounds.at(-1)?.round.requiredReviewerUserIds ?? [];
  const signOffActions =
    governance && programme.workflowState === "Approved"
      ? finalSignOffActions({
          mayLead: canSignLead && !leadSigned,
          mayQa: canSignQa && !qaSigned,
          mayComplete: can(session.subject, "workflow:transition", {
            type: "programme",
            programmeId: programme.id,
          }),
          signOffsRecorded: governance.requiredSignOffsRecorded,
        })
      : [];
  const workflowActions = [
    ...getWorkflowActions(
      session,
      queueProgramme,
      submissionBlocked,
      submissionBlockerCount,
      approvalBlocked,
      approvalBlockers,
      blockingReviewFindingCount,
      currentRoundReviewerUserIds,
      (governance?.activeReviewerUserIds.length ?? 0) > 0,
    ),
    ...signOffActions,
  ];
  const approvalActionAvailable = workflowActions.some((action) => action.id === "approve");
  const needsWorkflowNote = workflowActions.some((action) => action.requiresNote);
  const overrideAction = workflowActions.find((action) => action.id === overrideActionId);
  const overrideGate: ReadinessState =
    overrideActionId === "approve" ? "Ready for approval" : "Ready for internal review";
  const overrideBlockers = overrideActionId === "approve" ? approvalBlockers : submissionBlockers;
  const overrideEligibleAction =
    session.subject.accountRole === "client-admin"
      ? workflowActions.find(
          (action) =>
            (action.id === "submit-review" || action.id === "approve") &&
            action.blockedReason !== undefined &&
            (action.id === "approve" ? approvalBlockers : submissionBlockers).length > 0,
        )
      : undefined;

  const applyReadinessOverride = (): void => {
    if (!overrideAction?.nextState || !overrideReason.trim() || !overrideConfirmed) {
      return;
    }
    const reason = overrideReason.trim();
    const nextState = overrideAction.nextState;
    runAction(async () => {
      if (overrideAction.id === "approve") {
        const approvalError = validateInitialApprovalDetails(approvalDetails);
        if (approvalError) {
          throw new Error(approvalError);
        }
        const result = await client.approveProgramme({
          programmeId: programme.id,
          institutionalLabel: approvalDetails.institutionalLabel.trim(),
          effectiveFrom: approvalDetails.effectiveFrom,
          ...(approvalDetails.nextRevalidationDue
            ? { nextRevalidationDue: approvalDetails.nextRevalidationDue }
            : {}),
          ...(workflowNote.trim() ? { note: workflowNote.trim() } : {}),
          readinessOverride: { expectedSaveRevision: programme.saveRevision, reason },
        });
        setOverrideReference(result.readinessOverrideDecision?.id ?? null);
        // Cleared here alone: approval is the one override sent with the note.
        // A submission is overridden in development, where no action takes a
        // comment, so the field is not shown and there is no note to send.
        setWorkflowNote("");
      } else {
        const result = await client.transitionWorkflowWithOverride({
          programmeId: programme.id,
          nextState,
          readinessOverride: { expectedSaveRevision: programme.saveRevision, reason },
        });
        setOverrideReference(result.readinessOverrideDecision?.id ?? null);
      }
      closeOverride();
    });
  };

  const createRevision = async (): Promise<void> => {
    const snapshot = governance?.snapshots.at(-1);
    if (!snapshot) {
      return;
    }

    setActionPending(true);
    setError(null);
    try {
      const revision = await client.reviseFromSnapshot({
        snapshotId: snapshot.id,
        newId: uid("programme"),
      });
      await onRevisionCreated(revision.id);
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error("Unable to create a revision"));
    } finally {
      setActionPending(false);
    }
  };

  const addApplicability = async (): Promise<void> => {
    const governanceRevisionId = applicabilityRevisionId || approvedRevision?.id;
    if (!governanceRevisionId || !effectiveFrom) {
      return;
    }
    await runAction(async () => {
      await client.createRevisionApplicability({
        programmeId: programme.id,
        governanceRevisionId,
        effectiveFrom,
        publicationConsent,
      });
      setEffectiveFrom("");
    });
  };

  const selectApplicabilityRevision = (governanceRevisionId: string): void => {
    setApplicabilityRevisionId(governanceRevisionId);
    const existing = [...(governance?.revisionApplicability ?? [])]
      .filter((rule) => rule.governanceRevisionId === governanceRevisionId)
      .sort(
        (left, right) =>
          right.createdAt.localeCompare(left.createdAt) || right.id.localeCompare(left.id),
      )[0];
    setEffectiveFrom(existing?.effectiveFrom ?? "");
    setPublicationConsent(existing?.publicationConsent ?? true);
  };

  return {
    governance,
    setGovernance,
    reviewRounds,
    setReviewRounds,
    loading,
    setLoading,
    error,
    setError,
    actionPending,
    setActionPending,
    workflowNote,
    setWorkflowNote,
    ...overrideState,
    exportFormat,
    setExportFormat,
    expanded,
    setExpanded,
    ...applicabilityState,
    approvalDetails,
    setApprovalDetails,
    fetchGovernance,
    refresh,
    runAction,
    leadSigned,
    qaSigned,
    canSignLead,
    canSignQa,
    canExport,
    exportRefusal,
    canCreateRevision,
    submissionBlockers,
    submissionBlockerCount,
    submissionBlocked,
    approvalBlockers,
    approvalBlocked,
    approvalCandidate,
    submittedRevision,
    approvedRevision,
    canSetApplicability,
    navigationItems,
    stage,
    currentStationIndex,
    queueProgramme,
    blockingReviewFindingCount,
    currentRoundReviewerUserIds,
    workflowActions,
    approvalActionAvailable,
    needsWorkflowNote,
    overrideAction,
    overrideGate,
    overrideBlockers,
    overrideEligibleAction,
    applyReadinessOverride,
    createRevision,
    addApplicability,
    selectApplicabilityRevision,
  };
}
